<template>
    <v-row justify="center">
        <v-dialog v-model="dialog" fullscreen persistent hide-overlay transition="dialog-bottom-transition">
            <v-card outlined shaped class="elevation-4">
                <ValidationObserver ref="refPago" v-slot="{ invalid, validated }">
                    <v-form name="form" @submit.prevent="savePago">
                        <v-toolbar dark color="primary">
                            <v-btn icon dark @click.native="handleModal">
                                <v-icon>fas fa-times-circle</v-icon>
                            </v-btn>

                            <v-toolbar-title>
                                <div class="text-h6 font-weight-bold white--text">
                                    Registrar nuevo pago
                                </div>
                            </v-toolbar-title>

                            <v-spacer></v-spacer>

                            <v-toolbar-items>
                                <v-btn color="primary" type="submit" tile dark align="right"  :disabled="invalid || !validated || isSubmitting" :loading="isSubmitting">
                                    <v-icon class="ma-2" dense large left>fas fa-save</v-icon>Guardar pago 
                                </v-btn>
                            </v-toolbar-items>
                        </v-toolbar>

                        <v-card-text>
                            <v-row>
                                <v-col cols="12" sm="6" md="3" class="pa-0">
                                    <SingleCalendar refElement="paymentDate" title="Fecha de pago"
                                    :input-date.sync="pago.paymentDate" :disabled="true"/>
                                </v-col>

                                <v-col cols="12" sm="6" md="6">
                                    <ValidationProvider rules="required" v-slot="{ errors, valid}" :immediate="true">
                                        <v-autocomplete :items="providers" item-text="razonSocial" item-value="idProveedor"
                                        prepend-inner-icon="fas fa-outdent" hint="Seleccionar proveedor."
                                        v-model="pago.providerId" label="Proveedores" required outlined 
                                        :error-messages="errors" :success="valid">
                                        </v-autocomplete>
                                    </ValidationProvider>
                                </v-col>

                                <v-col cols="12" sm="6" md="3">
                                    <ValidationProvider rules="required|decimal|min_value:0.01" v-slot="{ errors, valid}" :immediate="true">
                                        <v-text-field type="number" label="Monto*" prepend-icon="fas fa-dollar-sign"
                                        required v-model.number="pago.payedAmount" :error-messages="errors" :success="valid">
                                        </v-text-field>
                                    </ValidationProvider>
                                </v-col>

                                <v-col cols="12" sm="6" md="3">
                                    <ValidationProvider rules="required" v-slot="{ errors, valid}" :immediate="true">
                                        <v-autocomplete :items="paymentMethod" item-text="name" item-value="value"
                                        v-model="pago.paymentMethod" label="Metodo de pago*" prepend-icon="fas fa-credit-card"
                                        :error-messages="errors" :success="valid">
                                        </v-autocomplete>
                                    </ValidationProvider>
                                </v-col>

                                <v-col cols="12" sm="6" md="4" v-if="enableOperationNumber">
                                    <ValidationProvider :rules="enableOperationNumber ? 'required': ''" v-slot="{ errors, valid }" :immediate="true">
                                        <v-text-field label="Numero/referencia de la operación" prepend-icon="fas fa-receipt"
                                        v-model="pago.operationNumber" :error-messages="errors" :success="valid" required :counter="100">
                                        </v-text-field>
                                    </ValidationProvider>
                                </v-col>
                                <v-col cols="12" sm="6" :md="enableOperationNumber ? '4' : '5'">
                                    <template>
                                        <v-file-input v-model="fileEvidences" color="deep-purple accent-4" counter
                                            accept="image/png, image/jpeg, image/bmp, application/pdf"
                                            label="Adjuntar fichero" :multiple="false" placeholder="Adjuntar fichero"
                                            prepend-icon="fas fa-paperclip" show-size>
                                                <template v-slot:selection="{ index, text }">
                                                    <v-chip v-if="index < 2" color="deep-purple accent-4" dark label small >
                                                        {{ text }}
                                                    </v-chip>
                                                    <span v-else-if="index === 2" class="text-overline grey--text text--darken-3 mx-2">
                                                        +{{ (fileEvidences?.length || 0) - 2 }} Archivos
                                                    </span>
                                                </template>
                                        </v-file-input>
                                    </template>
                                </v-col>
                                <v-col cols="3" sm="6" md="4" :class="enableOperationNumber ? 'justify-end' : 'mt-4'">
                                    <v-btn color="red" class="white--text font-weight-bold" disabled>
                                        Saldo pendiente: <span class="font-weight-bold text-h6"> {{ getTotalSaldoNatural }} </span>
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <AddPayment :pago="pago" @addPagos="addPagos" />
                        </v-card-text>
                    </v-form>
                </ValidationObserver>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
    import ProveedorPagoDataService from '@/service/catalogo/proveedor/pago/proveedorPagoDataService';
    import SingleCalendar from '@/components/shared/SingleCalendar.vue';
    import AddPayment from './AddPayment.vue';
    import { getToday } from '@/utils/DateUtil';
    import { getFormatCurrency, isEqualMoney } from '@/utils/CurrencyUtil';
import ProveedorEvidenceFileService from '@/service/entrada/ProveedorEvidenceFileService';
import { extractMessage } from '@/helpers/errorHelper';

    export default {
        name: "NuevoPago",
        props: {
            dialog: { type: Boolean, default: false },
            handleModal: { type: Function },
            providers: { type: Array, required: true },
            initialize: { type: Function }
        },
        components: { SingleCalendar, AddPayment },
        data() {
            return {
                paymentMethod: [ 'EFECTIVO', 'TRANSFERENCIA', 'CHEQUE'],
                pago: {
                    id: null,
                    clientId: null,
                    paymentDate: getToday().toFormat('yyyy-MM-dd'),
                    payedAmount: 0,
                    paymentMethod: '',
                    operationNumber: '',
                    payments: []
                },
                fileEvidences: undefined,
                isSubmitting: false,
            }
        },
        methods: {
            addPagos(pago) {
                this.pago.payments = pago;
            },
            async savePago() {
                if (this.isSubmitting) return;
                try {
                    const isValid = await this.$refs.refPago.validate();
                    if (!isValid) return;
                    if(!isEqualMoney(this.getPayedAmountTotal, this.pago.payedAmount)){
                        this.$swal.fire({ icon: 'warning', title: `No corresponde el monto a pagar ${this.getPayedAmountNatural}, con la suma de los pagos: ${this.getPayedAmountTotalNatural}`, showConfirmButton: false, timer: 2500 });
                        return;
                    }
                    this.isSubmitting = true;
                    const response = await ProveedorPagoDataService.store(this.pago);
                    if(this.fileEvidences != undefined){
                        await this.storeFileEvidences(response.data.id);
                    } else {
                        this.initialize();
                        this.handleModal();
                        this.$swal.fire({ icon: 'success', title: 'Se ha registrado el pago', showConfirmButton: false, timer: 2500 });
                    }
                } catch (error) {
                    this.$swal.fire({ icon: 'error', title: 'Pago no registrado', text: extractMessage(error), showConfirmButton: false, timer: 2500 });
                } finally {
                    this.isSubmitting = false;
                }
            },
            async storeFileEvidences(paymentProviderId){
                try {
                    const resp = await ProveedorEvidenceFileService.uploadEvidence(this.fileEvidences, paymentProviderId);
                    this.handleModal();
                    this.initialize();
                    this.$swal.fire({ position: 'top-end', icon: 'success', title: 'Pago registrado correctamente', text: resp.data.message, showConfirmButton: false, timer: 3500 });
                } catch (error) {
                    // El pago ya fue registrado: se cierra la ventana para no reintentar y duplicar el pago.
                    this.handleModal();
                    this.initialize();
                    this.$swal.fire({ position: 'top-end', icon: 'error', title: 'Pago registrado sin evidencia', text: 'El pago se registro correctamente, pero no se pudo subir la evidencia.', showConfirmButton: false, timer: 3500 });
                }
            },
        },
        computed: {
            getPayedAmountTotal() {
                const payments = this.pago.payments.filter(payment => payment.activo == true );
                return payments.reduce((total, payment) => total + payment.payedAmount, 0);
            },
            getPayedAmountTotalNatural() {
                return getFormatCurrency(this.getPayedAmountTotal, '$ ');
            },
            getPayedAmountNatural() {
                return getFormatCurrency(this.pago.payedAmount, '$ ');
            },
            enableOperationNumber(){
                return this.pago.paymentMethod == "TRANSFERENCIA" || this.pago.paymentMethod == "CHEQUE";
            },
            getTotalSaldoNatural(){
                const total = this.pago.payments.reduce((acc, item) => acc + item.saldo, 0);
                return getFormatCurrency(total, '$ ');
            },
        }
    }
</script>